import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookmark,
  faBookmark as faBookmarkSolid,
} from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import styles from "./Tile.module.css";
import { useAppDispatch, useAppSelector } from "../../app/store";
import {
  handleTileClickEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import type { RichTextDescription } from "../../utils/contentful-helpers";

interface TileProps {
  id: string;
  title: string;
  description: string | RichTextDescription;
  thumbnail: string;
  url: string;
  isResearchPage: boolean;
  onBookmark: () => void;
}

const Tile = ({
  id,
  title,
  description,
  thumbnail,
  url,
  isResearchPage,
  onBookmark,
}: TileProps) => {
  const { userId, sessionId } = useAppSelector((state) => state.session);
  const bookmarkedResources = useAppSelector(
    (state) => state.resourceBookmarks.bookmarks,
  );
  const isBookmarked = bookmarkedResources.some(
    (item) => item.resourceId === id,
  );

  const dispatch = useAppDispatch();

  const renderDescription = () => {
    if (typeof description === "string") {
      return <p className={styles.tileDescription}>{description}</p>;
    }

    const descriptionText =
      description.content?.[0]?.content?.[0]?.value ??
      "No description available";

    return <p className={styles.tileDescription}>{descriptionText}</p>;
  };

  const handleClickTile = () => {
    if (!userId || !sessionId) return;

    const eventData = handleTileClickEventBuilder(userId, sessionId, title, {});
    void dispatch(sendEvent(eventData));
  };

  return (
    <div
      className={[styles.tile, isResearchPage ? "research-page-tile" : ""].join(
        " ",
      )}
      data-cy="resource-tile"
      data-id={id}
    >
      <div className={styles.tileContent}>
        <a
          onClick={handleClickTile}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-cy="resource-tile-link"
        >
          <img src={thumbnail} alt={title} />
          <h3 className={styles.tileTitle}>{title}</h3>
        </a>
        {renderDescription()}
        <button
          onClick={onBookmark}
          className={styles.bookmarkButton}
          aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
          data-cy="resource-tile-bookmark-button"
        >
          <FontAwesomeIcon
            icon={isBookmarked ? faBookmarkSolid : faBookmark}
            className={cx(styles.bookmarkIcon,
              isBookmarked ? styles.bookmarkIconBlue : styles.bookmarkIconWhite)
            }
            data-tour="bookmark-icon"
          />
        </button>
      </div>
    </div>
  );
};

export default Tile;
